import * as z from 'zod'
import { resolveToolName, zodQs } from '../utils'
import type { UIMessage, LanguageModelUsage } from 'ai'
import { ConversationAnalysisSchema } from './ConversationAnalysis'
import {
    AgentBuiltInTool,
    ENABLE_TOOLS_TOOL,
    AgentGatewayOptionsSchema,
    AgentReasoningSchema,
    AgentWebSearchSchema,
    AgentSearchMode,
    AgentRankingSchema,
} from './Agent'
import { MIME_TYPE_REGEX } from './Media'
import type { PagePointer } from './PageAccess'
import type { PlatformContext } from './Platform'
import { UNAVAILABLE_TOOL_ERROR_PATTERN } from './StreamError'

import { DeviceContextSchema, type DeviceContext } from './DeviceContext'

export { DeviceContextSchema, type DeviceContext }

const TextPartSchema = z.object({
    type: z.literal('text'),
    text: z.string(),
})

const FilePartSchema = z.object({
    type: z.literal('file'),
    mediaType: z.string().regex(MIME_TYPE_REGEX),
    url: z.url(),
    filename: z.string().optional(),
    providerMetadata: z
        .record(z.string(), z.record(z.string(), z.unknown()))
        .optional(),
})

const MessagePartSchema = z.discriminatedUnion('type', [
    TextPartSchema,
    FilePartSchema,
])

export const ChatRequestSchema = z.object({
    id: z.string(),
    messages: z.array(
        z.object({
            id: z.string().optional(),
            role: z.enum(['assistant', 'user']),
            content: z.string(),
            parts: z.array(MessagePartSchema),
            createdAt: z.string().optional(),
        }),
    ),
    model: z.string().optional(),
    temperature: z.number().optional(),
    prompt: z.string().optional(),
    maxOutputTokens: z.number().positive().optional(),
    presencePenalty: z.number().min(-2).max(2).optional(),
    frequencyPenalty: z.number().min(-2).max(2).optional(),
    searchMode: z.enum(AgentSearchMode).optional(),
    limit: z.number().optional(),
    ranking: AgentRankingSchema.optional(),
    reasoning: AgentReasoningSchema,
    webSearch: AgentWebSearchSchema.optional(),
    maxStepCount: z.number().int().min(1).max(40).optional(),
    disabledTools: z.array(z.enum(AgentBuiltInTool)).optional(),
    fallbackModels: z.array(z.string()).optional(),
    gatewayOptions: AgentGatewayOptionsSchema.partial().optional(),
    deviceContext: DeviceContextSchema.optional(),
    externalContext: z.record(z.string(), z.unknown()).optional(),
    smoothStream: z.boolean().optional(),
    /**
     * Tools the embedding app declares for this request (spec `host-tools.md`).
     * Left unparsed on purpose: the service parses each one on its own and drops
     * what is invalid or not allowed, without failing the turn.
     */
    hostTools: z.array(z.unknown()).optional(),
    /** Present when the widget can run page tools (spec `page-tools.md`); parsed by the service. */
    pageTools: z.unknown().optional(),
})

export type ChatRequest = z.infer<typeof ChatRequestSchema>

/**
 * What the chat route validates on the way in.
 *
 * Deliberately not `ChatRequestSchema`: a turn resumed after a client tool or
 * a tool approval posts the assistant message back, whose parts are tool
 * calls, and the part union above accepts text and files only. Pinning the
 * body to it would answer 400 to a legitimate resume. So this validates the
 * envelope the turn cannot run without and lets the rest through untouched
 * (`loose`), which is also why the overrides the client sends still reach the
 * service.
 *
 * The id is minted by the client and read back as a row key: without it the
 * lookup ran with `undefined` and the route answered 500 instead of 400
 * (found 2026-09-21).
 */
export const ChatRequestEnvelopeSchema = z
    .object({
        id: z.string().min(1),
        messages: z.array(z.object({ role: z.string() }).loose()).min(1),
    })
    .loose()

export enum BlockedReasonCode {
    SevereAbuse = 'severe_abuse',
    HighRiskScore = 'high_risk_score',
    RepeatedWarnings = 'repeated_warnings',
}

export type ChatMetadata = {
    ipAddress: string
    /** HTTP `Origin` of the request, empty for a server-to-server caller. */
    origin: string
    userAgent: string
    /**
     * Non-default transport the conversation arrived through (for example
     * `openai-api`), so cost and usage reports can isolate it. Absent for the
     * web and widget chat.
     */
    transport?: string
}

/**
 * Channel a conversation arrived through, as the analytics slice it.
 *
 * Derived at read time and never stored: a platform conversation is identified
 * by `chats.metadata.platform` ({@link PlatformType}), every other non-web
 * surface by `chats.metadata.transport` ({@link ChatTransport}), and a chat
 * carrying neither marker is the web chat or the widget (the historical
 * default, which is why those rows have nothing to read). `reasoning_chats`
 * and `agent_endpoint_logs` have no metadata at all, so each of those tables
 * is a channel by itself.
 *
 * Not to be confused with {@link AgentChannel}, which says where an agent is
 * published (frontoffice, app, widget): this says where a conversation came
 * from.
 *
 * Only the platforms we actually run are listed. `PlatformType` declares five
 * more, and one of those appearing in the data is reported under its own raw
 * value rather than folded into a bucket, so a channel nobody expected shows
 * up in the dashboard instead of hiding inside `other`.
 */
export enum ChatChannel {
    /** Web chat and embeddable widget: no marker on the row. */
    Web = 'web',
    WhatsApp = 'whatsapp',
    Teams = 'teams',
    /** Spoken session opened by us, `transport = voice`. */
    Voice = 'voice',
    /** OpenAI-compatible REST surface, `transport = openai-api`. */
    Api = 'api',
    /** Every row of `reasoning_chats`: the reasoning endpoint. */
    Reasoning = 'reasoning',
    /** Every row of `agent_endpoint_logs`: the custom agent endpoints. */
    Endpoint = 'endpoint',
}

export const ChatChannelSchema = z.enum(ChatChannel)

export type ChatMessageActions =
    'show-info' | 'revise' | 'regenerate' | 'upvote' | 'downvote' | 'feedback'

export type Chat = z.infer<typeof ChatSchema>
export type ChatsQuerystring = z.infer<typeof ChatsQuerystringSchema>

export type UIChatMessageDeviceContext = DeviceContext

/** Kinds of tool group the working set decides on, one group per MCP server. */
export type ToolGroupKind = 'mcp' | 'api' | 'subagent'

/**
 * What the working set decided for a turn (spec tool-catalogue-working-set,
 * F4). Group ids are those of {@link ToolCatalogueRecord}; the groups the model
 * enabled later in the turn are read from its `enableTools` calls.
 */
export type ToolWorkingSetRecord = {
    /** `shadow` recorded the decision and exposed everything, `active` applied it. */
    mode: 'shadow' | 'active'
    /** Groups exposed from the first step: the judged ones and the seed. */
    selected: string[]
    /** Groups the conversation seeded, exposed whatever the judgment. */
    seeded: string[]
    /** Probability the evaluation model gave each judged group, two decimals. */
    probabilities: Record<string, number>
    latencyMs: number
    /**
     * Time the evaluation was given: short in active mode, where the turn
     * waits for it, longer in shadow mode, where it runs alongside.
     */
    budgetMs: number
    /** Probability from which a judged group is exposed. */
    threshold: number
    /**
     * What the evaluation cost in USD, the amount charged to the organization.
     * Absent when it failed: nothing is charged then.
     */
    cost?: number
    inputTokens?: number
    /**
     * The evaluation answered with an error or not within the budget, and
     * every group stayed exposed.
     */
    failed?: boolean
}

/**
 * The part of a turn's tool catalogue the working set acts on (spec
 * tool-catalogue-working-set, F2): which MCP servers, API tools and sub-agents
 * were eligible, identified by id and not by the public keys, which collide
 * across servers. Enough to make a measurement repeatable without storing the
 * schemas.
 */
export type ToolCatalogueRecord = {
    /** Short hash of the eligible keys and their groups: two turns with the same set agree on it. */
    fingerprint: string
    groups: Array<{
        id: string
        kind: ToolGroupKind
        name: string
        /**
         * Keys of the group offered this turn: what attributes a call in the
         * message to its group, since a key names a different server's tool
         * in another turn.
         */
        keys: string[]
    }>
    workingSet?: ToolWorkingSetRecord
}

/** One group of a turn's catalogue, read against what the turn did with it. */
export type ToolSelectionGroup = {
    id: string
    kind: ToolGroupKind
    name: string
    tools: number
    /** Absent when the evaluation failed. */
    probability?: number
    /** Exposed from the first step in active mode, or would have been in shadow mode. */
    selected: boolean
    seeded: boolean
    /**
     * Brought back during the turn, by `enableTools` or by a direct call to
     * one of its tools that the model was refused as unavailable.
     */
    enabled: boolean
    /**
     * The model called one of its tools, whatever came of the call (an error,
     * a denied approval): the turn needed the group.
     */
    used: boolean
}

export type ToolSelectionSummary = {
    decision: ToolWorkingSetRecord
    /** Most probable first, then by name. */
    groups: ToolSelectionGroup[]
    /**
     * Groups the turn used that the decision left out. In active mode the
     * model had to bring each one back, in shadow mode it would have had to:
     * the working set's miss, the figure F3 of the spec counts.
     */
    missed: number
}

type SummarizedPart = {
    type: string
    toolName?: string
    state?: string
    input?: unknown
    errorText?: string
}

/**
 * The working set's decision on a turn next to what the turn did: which
 * groups it exposed, which the model brought back and which it used. Read
 * from the stored answer, whose metadata carries the record and whose parts
 * carry the calls, the way the backend read them during the turn: a call
 * counts under the name the repair would give it, and an `enableTools` call
 * only once it ran. Undefined when the working set did not decide.
 */
export function summarizeToolSelection(message: {
    parts?: ReadonlyArray<unknown>
    metadata?: { toolCatalogue?: ToolCatalogueRecord }
}): ToolSelectionSummary | undefined {
    const record = message.metadata?.toolCatalogue
    const decision = record?.workingSet
    if (!record || !decision) {
        return undefined
    }
    const groupOfKey = new Map(
        record.groups.flatMap((group) =>
            group.keys.map((key) => [key, group.id] as const),
        ),
    )
    const groupByName = new Map(
        record.groups.map((group) => [group.name, group.id] as const),
    )
    const used = new Set<string>()
    const enabled = new Set<string>()
    for (const part of (message.parts ?? []) as SummarizedPart[]) {
        const toolName =
            part.type === 'dynamic-tool'
                ? part.toolName
                : part.type.startsWith('tool-')
                  ? part.type.slice(5)
                  : undefined
        if (!toolName) {
            continue
        }
        if (toolName === ENABLE_TOOLS_TOOL) {
            // A call whose input failed validation enabled nothing.
            const names =
                part.state === 'output-available'
                    ? (part.input as { groups?: unknown })?.groups
                    : undefined
            for (const name of Array.isArray(names) ? names : []) {
                const groupId = groupByName.get(String(name))
                if (groupId) {
                    enabled.add(groupId)
                }
            }
            continue
        }
        const key = resolveToolName(toolName, groupOfKey.keys())
        const groupId = key && groupOfKey.get(key)
        if (!groupId) {
            continue
        }
        // A call to a hidden tool is refused as unavailable, and that refusal
        // is how the model asked for its group back.
        if (
            part.state === 'output-error' &&
            UNAVAILABLE_TOOL_ERROR_PATTERN.test(part.errorText ?? '')
        ) {
            enabled.add(groupId)
        } else {
            used.add(groupId)
        }
    }
    const selected = new Set(decision.selected)
    const seeded = new Set(decision.seeded)
    const groups = record.groups
        .map((group) => ({
            id: group.id,
            kind: group.kind,
            name: group.name,
            tools: group.keys.length,
            probability: decision.probabilities[group.id],
            selected: selected.has(group.id),
            seeded: seeded.has(group.id),
            enabled: enabled.has(group.id),
            used: used.has(group.id),
        }))
        .sort(
            (a, b) =>
                (b.probability ?? -1) - (a.probability ?? -1) ||
                a.name.localeCompare(b.name),
        )
    return {
        decision,
        groups,
        missed: groups.filter((group) => group.used && !group.selected).length,
    }
}

export type UIChatMessageMetadata = {
    /** Unix timestamp (ms) when the stream started */
    createdAt?: number
    /** Unix timestamp (ms) when the stream finished */
    completedAt?: number
    /** Canonical model ID used for the request (e.g. `google/gemini-3-flash`) */
    model?: string
    /** Total tokens consumed across all steps */
    totalTokens?: number
    /** Per-tool-call durations in ms, keyed by tool call ID */
    partTimings?: Record<string, number>
    /** Client device context sent with the request */
    deviceContext?: UIChatMessageDeviceContext
    /** Platform channel context (WhatsApp/Teams), the device-context equivalent for platform turns */
    platformContext?: PlatformContext
    /** External context provided by the host application and validated server-side */
    externalContext?: Record<string, unknown>
    /** Provider that ultimately served the request (e.g. `vertex`, `openai`) */
    resolvedProvider?: string
    /**
     * Every provider the gateway sent this message's requests to, across all
     * steps and attempts (failed attempts included), in the order they were
     * first contacted. `resolvedProvider` keeps only the last step's. A
     * gateway call that fails as a whole returns no metadata, so its attempts
     * are not listed; a sub-agent's providers are on its own log.
     */
    routedProviders?: string[]
    /** Actual model ID as exposed by the resolved provider (e.g. `gemini-3-flash-preview`) */
    resolvedModelId?: string
    /** Market cost of the request in USD, as a numeric string */
    cost?: string
    /** Number of model-level routing attempts made by the gateway */
    modelAttemptCount?: number
    /** Total number of provider-level attempts (includes retries across providers) */
    totalProviderAttemptCount?: number
    /** Whether Zero Data Retention was active for this request */
    zeroDataRetention?: boolean
    /** Whether the provider was instructed not to use the prompt for model training */
    disallowPromptTraining?: boolean
    /** Vercel AI Gateway generation ID, useful for tracing in the Vercel dashboard */
    generationId?: string
    /** Tokens served from the provider's prompt cache (e.g. Gemini context caching) */
    cachedTokens?: number
    /** Provider traffic tier (e.g. `PROVISIONED_THROUGHPUT`, `ON_DEMAND`) */
    trafficType?: string
    /**
     * Transport the message travelled through ({@link ChatTransport}), when it
     * was not the web chat itself: `voice` marks a spoken exchange (the user
     * text is the call transcription, the assistant text was read aloud),
     * `openai-api` an API turn. Absent on plain web messages.
     */
    transport?: string
    /**
     * The MCP servers, API tools and sub-agents this turn could use, and the
     * working set's decision on them. Absent when the agent has none. Kept
     * from a public reader: streamed only to the organization's own audiences
     * (on the closing finish), and removed from whatever else leaves the
     * organization ({@link withoutToolCatalogue}).
     */
    toolCatalogue?: ToolCatalogueRecord
    /**
     * The evaluation of the turn after the reply (spec post-turn-evaluation):
     * a probability per question. Written after the answer, and removed from
     * a public read ({@link withoutTurnEvaluation}).
     */
    turnEvaluation?: TurnEvaluationRecord
    /**
     * Set on the user message a wakeup injected (spec agent-wakeup): its text
     * is the prompt the agent left for itself, not words the user typed.
     */
    wakeup?: UIChatMessageWakeup
    /**
     * What the user pointed at on the page with this message (spec
     * `page-point-and-choose.md`, P). The backend keeps it only from a
     * widget that reads the page, and the model reads it as text after the
     * message's own.
     */
    pagePointer?: PagePointer
}

/** The wakeup a seed message belongs to (spec agent-wakeup). */
export type UIChatMessageWakeup = {
    id: string
    loopId: string
    iteration: number
    reason: string
}

/**
 * The questions of the post-turn evaluation, in the order they are asked and
 * listed. The backend's questions are typed against it, so the two cannot
 * drift. A reader orders by this list and not by the record: jsonb keeps the
 * keys of an object sorted by length, not in the order they were written.
 */
export const TURN_EVALUATION_QUESTION_IDS = [
    'durableFact',
    'abusive',
    'manipulation',
    'asksHuman',
    'missingInfo',
    'informationGap',
] as const

export type TurnEvaluationQuestionId =
    (typeof TURN_EVALUATION_QUESTION_IDS)[number]

/**
 * The evaluation model's answers on a finished turn (spec
 * post-turn-evaluation, F1). Recorded in shadow only: nothing reads it yet.
 */
export type TurnEvaluationRecord = {
    /**
     * The wording of the questions: a threshold holds only for the wording
     * it was measured on, so a record says which one it answered.
     */
    version: number
    mode: 'shadow'
    /** Probability per question, two decimals. Empty when the request failed. */
    probabilities: Record<string, number>
    latencyMs: number
    budgetMs: number
    /** Market cost of the request in USD. */
    cost?: number
    inputTokens?: number
    /** The request failed or ran past its budget: no answer was recorded. */
    failed?: true
}

export type UIChatMessage = UIMessage & {
    metadata?: UIChatMessageMetadata
}

/**
 * The history without the turn evaluations, for a public read: a probability
 * of abuse on a user must not reach the widget. Kept apart from
 * {@link withoutToolCatalogue}, which the data export uses too, because
 * whether the export includes this judgment is still to be decided.
 */
export function withoutTurnEvaluation(
    messages: UIChatMessage[],
): UIChatMessage[] {
    return messages.map((message) => {
        if (!message.metadata?.turnEvaluation) {
            return message
        }
        const { turnEvaluation: _turnEvaluation, ...metadata } =
            message.metadata
        return { ...message, metadata }
    })
}

/**
 * The history without the tool catalogue records, for a reader outside the
 * organization (a public chat read, the data export): a record names every
 * group the agent has, used or not.
 */
export function withoutToolCatalogue(
    messages: UIChatMessage[],
): UIChatMessage[] {
    return messages.map((message) => {
        if (!message.metadata?.toolCatalogue) {
            return message
        }
        const { toolCatalogue: _toolCatalogue, ...metadata } = message.metadata
        return { ...message, metadata }
    })
}

/**
 * States in which a tool invocation part waits for nothing more.
 *
 * Two readers, one meaning: the backend leaves such a part alone instead of
 * forcing it to a terminal state (`completeUnresolvedToolInvocations`), and the
 * web client counts it as answered when deciding whether to resume a suspended
 * turn (`sendAutomaticallyWhen` in `useChatbotStore`). They used to keep a copy
 * each, with different membership.
 */
export const TOOL_TERMINAL_STATES: ReadonlySet<string> = new Set([
    'output-available',
    'output-error',
    'output-denied',
])

export const ChatSchema = z.object({
    id: z.string(),
    messages: z.array(z.unknown()) as unknown as z.ZodType<UIChatMessage[]>,
    title: z.string().nullable(),
    summary: z.string().nullable(),
    messageCountAtLastSummary: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    metadata: z.record(z.string(), z.unknown()),
    totalCostInUSD: z.string(),
    usage: z.unknown() as unknown as z.ZodType<LanguageModelUsage>,
    agentId: z.string().nullable(),
    userId: z.string(),
    organizationId: z.string(),
    deleted: z.boolean(),
    deletedAt: z.coerce.date().nullable(),
    deletedBy: z.string().nullable(),
    conversationAnalysis: ConversationAnalysisSchema.nullable().optional(),
    blocked: z.boolean(),
    blockedAt: z.coerce.date().nullable(),
    blockedReasonCode: z.enum(BlockedReasonCode).nullable(),
    blockedReason: z.string().nullable(),
    warningCount: z.number(),
    lastMessageAt: z.coerce.date(),
    /**
     * A turn is running in the chat: its question is already among the
     * messages, its answer streams from the resume endpoint (spec
     * resumable-chat-streams). Only the read of a single chat sets it.
     */
    turnInProgress: z.boolean().optional(),
})

export const ChatsQuerystringSchema = z.object({
    ...zodQs.pagination(),
    ...zodQs.sort(['updatedAt', 'createdAt', 'lastMessageAt']),
    ...zodQs.filters(['agentId', 'userId']),
    ...zodQs.range('updatedAt'),
    ...zodQs.range('lastMessageAt'),
    ...zodQs.ids(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    /** Text in the chat's title (its summary without one), any case */
    search: z.string().trim().max(200).optional(),
})

export const ChatsPublicSchema = z.object({
    id: z.string(),
    agentId: z.string().optional(),
    organizationId: z.string(),
    summary: z.string().optional(),
    title: z.string().optional(),
    userId: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    lastMessageAt: z.coerce.date(),
    messages: z.array(z.unknown()),
    blocked: z.boolean(),
    turnInProgress: z.boolean().optional(),
})

export type ChatPublic = z.infer<typeof ChatsPublicSchema>
